import { PeaksInstance, ZoomChangeEvent } from "peaks.js"
import { emitEvent, Emitter } from "@/services/event-emitter.ts"
import Konva from "konva"
import {
  getMaxZoomLevel, getPeaksInstance, getZoomview, WAVEFORM_OVERVIEW_TYPE_NAME, WaveformZoomviewType,
} from "@/features/AudioWorkstation/utils/audio-editor-utils.ts"
import { EventTypes } from "@/types/types.ts"
import { RefObject } from "react"
import { getClientX } from "@/utils/utils.ts";
import KonvaEventObject = Konva.KonvaEventObject;

export class MouseDragHandlerExtension {
  overview: WaveformZoomviewType
  dragZoomSide: string | undefined
  isDragZooming = false
  overrideMouseDown = false
  currentHighlightLayerWidth: number | undefined
  currentZoomLevel: number | undefined
  currentHighlightLayerLeftSideX: number | undefined
  currentHighlightLayerRightSideX: number | undefined
  currentHighlightLayerOffsetX: number | undefined

  constructor(
    public readonly peaksInstance: PeaksInstance,
    public readonly overviewContainer: RefObject<HTMLDivElement | null>,
  ) {
    this.peaksInstance = peaksInstance
    this.overviewContainer = overviewContainer
    this.overview = peaksInstance.views.getView(WAVEFORM_OVERVIEW_TYPE_NAME) as WaveformZoomviewType

    /* if (!this.overview) {
      throw Error("Warning: no overview container found")
    } */

    try {
      this.updateHighlightLayerData()
    } catch (error) {
      console.warn(error)
    }
  }

  addWindowEventListeners = () => {
    // Use the window mousemove and mouseup handlers instead of the
    // Konva.Stage ones so that we still receive events if the user moves the
    // mouse outside the stage.
    window.addEventListener("mouseup", this.onMouseUp)
    window.addEventListener("mousemove", this.onMouseMove)
    window.addEventListener("mouseup", this.handleMouseUp)
  }

  removeWindowEventListeners = () => {
    window.removeEventListener("mouseup", this.onMouseUp)
    window.removeEventListener("mousemove", this.onMouseMove)
    window.removeEventListener("mouseup", this.handleMouseUp)
  }

  addEventEmitterListeners = () => {
    Emitter
      .on(EventTypes.ZOOM_FULL, this.updateHighlightLayerData)
      .on(EventTypes.ZOOM_IN, this.updateHighlightLayerData)
      .on(EventTypes.ZOOM_OUT, this.updateHighlightLayerData)
  }

  removeEventEmitterListeners = () => {
    Emitter
      .off(EventTypes.ZOOM_FULL, this.updateHighlightLayerData)
      .off(EventTypes.ZOOM_IN, this.updateHighlightLayerData)
      .off(EventTypes.ZOOM_OUT, this.updateHighlightLayerData)
  }

  addPeaksEventListeners = () => {
    const overviewStage = this.overview._stage
    const element = this.overviewContainer.current as HTMLElement

    element.addEventListener("wheel", this.onWheelOverview, { passive: false })

    /** clear the default behavior (= seek directly to clicked/dragged cursor position) **/
    overviewStage.off("mousedown")
    overviewStage.on("mousedown", this.onMouseDown)

    this.peaksInstance.on("zoom.update", this.updateCurrentZoomLevel)
    this.overview._stage.on("wheel", this.updateHighlightLayerData)
    this.getHighlightLayerRect()
      .on("transformend", this.updateHighlightLayerData)
  }

  removePeaksEventListeners = () => {
    const element = this.overviewContainer.current
    element?.removeEventListener("wheel", this.onWheelOverview)
  }

  handleMouseUp = () => {
    emitEvent(EventTypes.ZOOM_CHANGED, {})
  }

  onWheelOverview = () => {
    this.updateHighlightLayerData()
  }

  onMouseUp = () => {
    this.isDragZooming = false
    this.updateHighlightLayerData()
  }
  onMouseDown = (e: KonvaEventObject<MouseEvent>) => {
    if (!this.overrideMouseDown) {
      return this.originalMouseDownHandler(e)
    }

    return this.mouseDownOverride()
  }
  onMouseMove = (e: MouseEvent) => {
    if (this.isDragZooming) {
      const layerX = getClientX(e)
      const newZoomLevel = this.getZoomLevel(e)
      const highlightRect = this.getHighlightLayerRect()
      const peaksInstance = getPeaksInstance(this.peaksInstance)
      const scrollbar = peaksInstance.views._scrollbar
      const scrollbox = peaksInstance.views._scrollbar._scrollbox

      this.currentZoomLevel = newZoomLevel

      getZoomview(this.peaksInstance)
        .setZoom({
          scale: newZoomLevel,
        })

      if (this.dragZoomSide === "right") {
        const x = Math.ceil(this.currentHighlightLayerOffsetX as number)
        scrollbox.x(x)
        scrollbar._updateWaveform(x)

        highlightRect.x(x)
      }

      if (this.dragZoomSide === "left") {
        const x = Math.ceil(layerX)
        scrollbox.x(x)
        scrollbar._updateWaveform(x)

        highlightRect.x(x)
      }

      return
    }

    const pointerAtLeftSide = this.pointerAtLeftSide(e)
    const pointerAtRightSide = this.pointerAtRightSide(e)
    const pointerAtLeftOrRightSide = pointerAtLeftSide || pointerAtRightSide
    const stage = this.overview._stage
    if (!stage) {
      return
    }

    const highlightLayerContainer = stage.container()
    highlightLayerContainer.style.cursor = pointerAtLeftOrRightSide ? "col-resize" : "default"

    this.overrideMouseDown = pointerAtLeftOrRightSide
    this.dragZoomSide = pointerAtLeftSide ? "left" : "right"
  }
  private updateHighlightLayerData = () => {
    this.updateCurrentHighlightLayerWidth()
    this.updateCurrentHighlightLayerLeftSideX()
    this.updateCurrentHighlightLayerRightSideX()
    this.updateCurrentHighlightLayerOffsetX()
  }
  private updateCurrentZoomLevel = (e: ZoomChangeEvent) => {
    this.currentZoomLevel = e.currentZoom
  }
  private updateCurrentHighlightLayerWidth = () => {
    this.currentHighlightLayerWidth = this.getHighlightLayerRect()
      .width()
  }
  private updateCurrentHighlightLayerOffsetX = () => {
    const highlightRect = this.getHighlightLayerRect()

    this.currentHighlightLayerOffsetX = highlightRect.x()
  }
  private updateCurrentHighlightLayerLeftSideX = () => {
    const highlightRect = this.getHighlightLayerRect()

    this.currentHighlightLayerLeftSideX = highlightRect.x()
  }
  private updateCurrentHighlightLayerRightSideX = () => {
    const highlightRect = this.getHighlightLayerRect()
    const highlightRectX = highlightRect.x()

    this.currentHighlightLayerRightSideX = highlightRectX + highlightRect.width()
  }
  private originalMouseDownHandler = (e: KonvaEventObject<MouseEvent>) => {
    this.isDragZooming = false

    this.overview?._mouseDragHandler?._mouseDragHandler._mouseDown(e)
  }
  private mouseDownOverride = () => {
    this.isDragZooming = true
  }

  private getHighlightLayer() {
    if (!this.overview) {
      throw Error("Warning: overview is not defined.")
    }
    return this.overview._highlightLayer
  }

  private getHighlightLayerRect() {
    return this.getHighlightLayer()._highlightRect
  }

  // TODO: refactor e.layerX because it's a non-standard feature
  // See https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/layerX
  // When this is done, the "e: any" signature can be refactored to "e: MouseEvent"
  private pointerAtLeftSide(e: MouseEvent) {
    const highlightRect = this.getHighlightLayerRect()
    const highlightRectX = highlightRect.x()
    const layerX = getClientX(e)

    return layerX >= highlightRectX && layerX <= highlightRectX + 10
  }

  private pointerAtRightSide(e: MouseEvent) {
    const highlightRect = this.getHighlightLayerRect()
    const highlightRectX = highlightRect.x()
    const highlightRectXEnd = highlightRectX + highlightRect.width()
    const layerX = getClientX(e)

    return layerX >= highlightRectXEnd - 10 && layerX <= highlightRectXEnd
  }

  private getXPixelsMoved = (e: MouseEvent) => {
    const x = getClientX(e)

    if (this.dragZoomSide === "left") {
      return x - (this.currentHighlightLayerLeftSideX as number)
    }

    // use with drag+shift:
    // const highlightRectX = highlightRect.x();
    // const highlightRectXEnd = highlightRectX + this.currentHighlightLayerWidth;

    return (this.currentHighlightLayerRightSideX as number) - x
  }
  private getHighlightLayerWidth = (e: MouseEvent) => {
    const MIN_HIGHLIGHT_LAYER_WIDTH = 45
    const newHighlightLayerWidth =
      (this.currentHighlightLayerWidth as number) - this.getXPixelsMoved(e)

    return newHighlightLayerWidth < MIN_HIGHLIGHT_LAYER_WIDTH
      ? MIN_HIGHLIGHT_LAYER_WIDTH
      : newHighlightLayerWidth
  }

  private getZoomLevel = (e: MouseEvent) => {
    const highlightRect = this.getHighlightLayerRect()
    const MAX_HIGHLIGHT_LAYER_WIDTH = highlightRect.width()

    const MIN_ZOOM_LEVEL = 256

    const newHighlightLayerWidth = this.getHighlightLayerWidth(e)
    const maxZoomLevel = getMaxZoomLevel(this.peaksInstance)
    const MAX_ZOOM_LEVEL = this.currentZoomLevel ?? maxZoomLevel

    if (!this.currentZoomLevel) {
      this.currentZoomLevel = maxZoomLevel
    }

    const normalizedWidth = newHighlightLayerWidth / MAX_HIGHLIGHT_LAYER_WIDTH
    const newZoomLevel = MIN_ZOOM_LEVEL + normalizedWidth * (MAX_ZOOM_LEVEL - MIN_ZOOM_LEVEL)

    if (newZoomLevel < MIN_ZOOM_LEVEL) {
      return MIN_ZOOM_LEVEL
    }

    return Math.ceil(newZoomLevel)
  }
}
